'use strict'

const KMZ_PAYLOAD_SCOPE_PRIORITY = {
  templateExport: ['template', 'waylines'],
  waylinesExport: ['waylines', 'template'],
  playback: ['template', 'waylines']
}

const KMZ_PAYLOAD_CHILDREN = [
  'payloadPositionIndex',
  'meteringMode',
  'dewarpingEnable',
  'returnMode',
  'samplingRate',
  'scanningMode',
  'modelColoringEnable'
]

const KMZ_PAYLOAD_ROOT_CACHE_PATHS = [
  'Folder.wpml:payloadParam',
  'Folder.payloadParam',
  'wpml:payloadParam',
  'payloadParam'
]

const KMZ_PAYLOAD_SCOPED_CACHE_PATHS = [
  'Folder.wpml:payloadParam',
  'Folder.payloadParam',
  'wpml:payloadParam',
  'payloadParam'
]

const fnClonePayloadValue = function (value) {
  if (Array.isArray(value)) {
    return value.map(fnClonePayloadValue)
  }
  if (value && typeof value === 'object') {
    const out = {}
    Object.keys(value).forEach((key) => {
      out[key] = fnClonePayloadValue(value[key])
    })
    return out
  }
  return value
}

const fnGetByPath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return undefined
    current = current[parts[i]]
  }
  return current
}

const fnSetByPath = function (obj, path, value) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  let current = obj
  for (let i = 0; i < parts.length - 1; i += 1) {
    const key = parts[i]
    if (!current[key] || typeof current[key] !== 'object' || Array.isArray(current[key])) {
      current[key] = {}
    }
    current = current[key]
  }
  current[parts[parts.length - 1]] = fnClonePayloadValue(value)
}

const fnDeletePath = function (obj, path) {
  const parts = String(path || '').split('.').filter(Boolean)
  if (!parts.length || !obj || typeof obj !== 'object') return
  const stack = [{ parent: null, key: '', value: obj }]
  let current = obj
  for (let i = 0; i < parts.length; i += 1) {
    if (!current || typeof current !== 'object') return
    const key = parts[i]
    if (!Object.prototype.hasOwnProperty.call(current, key)) return
    current = current[key]
    stack.push({ parent: stack[stack.length - 1].value, key: key, value: current })
  }
  const leaf = stack.pop()
  if (!leaf || !leaf.parent || !leaf.key) return
  delete leaf.parent[leaf.key]
  while (stack.length > 1) {
    const node = stack.pop()
    if (!node || !node.parent || !node.key) continue
    const value = node.parent[node.key]
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).length) break
    delete node.parent[node.key]
  }
}

const fnPickFirstDefined = function (source, paths) {
  const safeSource = (source && typeof source === 'object') ? source : {}
  const list = Array.isArray(paths) ? paths : []
  for (let i = 0; i < list.length; i += 1) {
    const path = String(list[i] || '').trim()
    if (!path) continue
    const value = fnGetByPath(safeSource, path)
    if (value === undefined) continue
    return value
  }
  return undefined
}

const fnAssignScopedPayloadSource = function (target, targetPath, source, sourcePaths) {
  const value = fnPickFirstDefined(source, sourcePaths)
  if (value === undefined) return
  fnSetByPath(target, targetPath, value)
}

const fnBuildScopePayloadSource = function (pointProps, folderProps) {
  const out = {}
  fnAssignScopedPayloadSource(out, 'Folder.wpml:payloadParam', pointProps, [
    'Folder.wpml:payloadParam',
    'Folder.payloadParam',
    'wpml:payloadParam',
    'payloadParam'
  ])
  fnAssignScopedPayloadSource(out, 'Folder.wpml:payloadParam', folderProps, [
    'wpml:payloadParam',
    'payloadParam',
    'Folder.wpml:payloadParam',
    'Folder.payloadParam'
  ])
  return out
}

const fnPruneKmzPayloadCache = function (props) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return
  KMZ_PAYLOAD_ROOT_CACHE_PATHS.forEach((path) => {
    fnDeletePath(safeProps, path)
  })
  ;['template', 'waylines'].forEach((scopeId) => {
    KMZ_PAYLOAD_SCOPED_CACHE_PATHS.forEach((path) => {
      fnDeletePath(safeProps, scopeId + '.' + path)
    })
  })
}

const buildKmzScopedPayloadPropsFromSources = function ({
  templatePointProps,
  waylinePointProps,
  templateFolderProps,
  waylinesFolderProps
} = {}) {
  return {
    template: fnBuildScopePayloadSource(templatePointProps, templateFolderProps),
    waylines: fnBuildScopePayloadSource(waylinePointProps, waylinesFolderProps)
  }
}

const applyKmzScopedPayloadProps = function (props, scopedPayloadProps) {
  const safeProps = (props && typeof props === 'object') ? props : null
  if (!safeProps) return safeProps
  const scoped = (scopedPayloadProps && typeof scopedPayloadProps === 'object') ? scopedPayloadProps : {}
  fnPruneKmzPayloadCache(safeProps)
  ;['template', 'waylines'].forEach((scopeId) => {
    const scopeProps = scoped[scopeId]
    if (!scopeProps || typeof scopeProps !== 'object') return
    fnAssignScopedPayloadSource(safeProps, scopeId + '.Folder.wpml:payloadParam', scopeProps, [
      'Folder.wpml:payloadParam',
      'Folder.payloadParam',
      'wpml:payloadParam',
      'payloadParam'
    ])
  })
  return safeProps
}

const syncKmzPointPayloadScopesFromXml = function (point, {
  fnXmlToObj,
  templateFolderProps,
  waylinesFolderProps,
  templateFolderEl,
  waylinesFolderEl
} = {}) {
  if (!point || typeof fnXmlToObj !== 'function') return point
  if (!point.props || typeof point.props !== 'object') point.props = {}
  const templatePointProps = point.xmlEl ? fnXmlToObj(point.xmlEl) : {}
  const waylinePointEl = Array.isArray(point.linkedXmlEls) ? (point.linkedXmlEls[0] || null) : null
  const waylinePointProps = waylinePointEl ? fnXmlToObj(waylinePointEl) : {}
  const scoped = buildKmzScopedPayloadPropsFromSources({
    templatePointProps: templatePointProps,
    waylinePointProps: waylinePointProps,
    templateFolderProps: templateFolderProps || (templateFolderEl ? fnXmlToObj(templateFolderEl) : {}),
    waylinesFolderProps: waylinesFolderProps || (waylinesFolderEl ? fnXmlToObj(waylinesFolderEl) : {})
  })
  applyKmzScopedPayloadProps(point.props, scoped)
  return point
}

const syncKmzMissionPayloadScopesFromXml = function (missionState, fnXmlToObj) {
  if (!missionState || !Array.isArray(missionState.waypoints) || typeof fnXmlToObj !== 'function') return missionState
  const templateFolderProps = missionState.folderEl ? fnXmlToObj(missionState.folderEl) : {}
  const waylinesFolderProps = missionState.secondaryFolderEl ? fnXmlToObj(missionState.secondaryFolderEl) : {}
  missionState.waypoints.forEach((point) => {
    syncKmzPointPayloadScopesFromXml(point, {
      fnXmlToObj: fnXmlToObj,
      templateFolderProps: templateFolderProps,
      waylinesFolderProps: waylinesFolderProps
    })
  })
  return missionState
}

const fnNormalizePayloadPathForCheck = function (path) {
  let normalized = String(path || '').trim()
  if (!normalized) return ''
  const lower = normalized.toLowerCase()
  if (lower.indexOf('template.') === 0) normalized = normalized.slice(9)
  if (String(normalized).toLowerCase().indexOf('waylines.') === 0) normalized = normalized.slice(9)
  return String(normalized || '').trim().toLowerCase()
}

const isKmzPayloadFieldPath = function (path) {
  const normalized = fnNormalizePayloadPathForCheck(path)
  if (!normalized) return false
  return normalized.indexOf('wpml:payloadparam') === 0 ||
    normalized.indexOf('payloadparam') === 0 ||
    normalized.indexOf('folder.wpml:payloadparam') === 0 ||
    normalized.indexOf('folder.payloadparam') === 0
}

const isKmzGlobalPayloadFieldPath = function (path) {
  return isKmzPayloadFieldPath(path)
}

const fnResolvePayloadValueByUseCase = function (pointOrProps, useCase, childName, fallbackValue) {
  const point = (pointOrProps && pointOrProps.props && typeof pointOrProps.props === 'object') ? pointOrProps : null
  const props = point ? point.props : ((pointOrProps && typeof pointOrProps === 'object') ? pointOrProps : {})
  const scopePriority = KMZ_PAYLOAD_SCOPE_PRIORITY[useCase] || KMZ_PAYLOAD_SCOPE_PRIORITY.templateExport
  const child = String(childName || '').trim()
  if (!child) return fallbackValue
  for (let i = 0; i < scopePriority.length; i += 1) {
    const scopeId = scopePriority[i]
    const paths = [
      scopeId + '.Folder.wpml:payloadParam.wpml:' + child,
      scopeId + '.Folder.wpml:payloadParam.' + child,
      scopeId + '.Folder.payloadParam.wpml:' + child,
      scopeId + '.Folder.payloadParam.' + child,
      scopeId + '.wpml:payloadParam.wpml:' + child,
      scopeId + '.wpml:payloadParam.' + child,
      scopeId + '.payloadParam.wpml:' + child,
      scopeId + '.payloadParam.' + child
    ]
    const value = fnPickFirstDefined(props, paths)
    if (value === undefined || value === null) continue
    if (typeof value === 'string' && !String(value).trim()) continue
    return value
  }
  return fallbackValue
}

const resolveKmzTemplateExportPayloadField = function (pointOrProps, childName, fallbackValue) {
  return fnResolvePayloadValueByUseCase(pointOrProps, 'templateExport', childName, fallbackValue)
}

const resolveKmzWaylinesExportPayloadField = function (pointOrProps, childName, fallbackValue) {
  return fnResolvePayloadValueByUseCase(pointOrProps, 'waylinesExport', childName, fallbackValue)
}

const resolveKmzPlaybackPayloadField = function (pointOrProps, childName, fallbackValue) {
  return fnResolvePayloadValueByUseCase(pointOrProps, 'playback', childName, fallbackValue)
}

const applyKmzResolvedPayloadDisplayValuesToMap = function (map, point, useCase) {
  const targetMap = (map && typeof map === 'object') ? map : {}
  const safeUseCase = String(useCase || 'playback').trim()
  KMZ_PAYLOAD_CHILDREN.forEach((childName) => {
    const value = fnResolvePayloadValueByUseCase(point, safeUseCase, childName, undefined)
    if (value === undefined) return
    targetMap['wpml:payloadParam.wpml:' + childName] = value
  })
  return targetMap
}

export {
  KMZ_PAYLOAD_CHILDREN,
  buildKmzScopedPayloadPropsFromSources,
  applyKmzScopedPayloadProps,
  syncKmzPointPayloadScopesFromXml,
  syncKmzMissionPayloadScopesFromXml,
  isKmzPayloadFieldPath,
  isKmzGlobalPayloadFieldPath,
  resolveKmzTemplateExportPayloadField,
  resolveKmzWaylinesExportPayloadField,
  resolveKmzPlaybackPayloadField,
  applyKmzResolvedPayloadDisplayValuesToMap
}
